// frontend/src/hooks/useCurrentBasho.js
import { useState, useEffect } from 'react'
import api from '../api'

// Fallback: compute likely current basho from calendar
function getFallbackBasho() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  const bashoMonths = [1, 3, 5, 7, 9, 11]
  // Find the next basho month
  const next = bashoMonths.find(m => m >= month) || bashoMonths[0]
  const y = next < month ? year + 1 : year
  return `${y}${String(next).padStart(2, '0')}`
}

let globalCache = null
let globalFetchedAt = null
const CACHE_TTL = 60 * 60 * 1000 // 1 hour

// Compute days until basho using the user's local calendar date vs the basho's
// UTC date (the canonical Japan date), so the countdown reflects what the user
// sees on their own calendar rather than the server's UTC clock.
function localDaysUntil(startDateStr) {
  if (!startDateStr) return null
  const start = new Date(startDateStr)
  // Treat the UTC date of startDate as the basho's calendar date
  const bashoDay = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate())
  // Treat the user's local date components as a calendar date
  const now = new Date()
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  const diff = Math.round((bashoDay - today) / (1000 * 60 * 60 * 24))
  return diff > 0 ? diff : null
}

export function useCurrentBasho() {
  const [basho, setBasho] = useState(null)
  const [day, setDay] = useState(1)
  const [status, setStatus] = useState('loading')
  const [startDate, setStartDate] = useState(null)

  useEffect(() => {
    // Use cache if fresh
    if (globalCache && globalFetchedAt && Date.now() - globalFetchedAt < CACHE_TTL) {
      setBasho(globalCache.basho)
      setDay(globalCache.day || 1)
      setStatus(globalCache.status || 'unknown')
      setStartDate(globalCache.startDate || null)
      return
    }

    api.get('/basho/current')
      .then(res => {
        const data = res.data
        globalCache = data
        globalFetchedAt = Date.now()
        setBasho(data.basho)
        setDay(data.day || 1)
        setStatus(data.status || 'unknown')
        setStartDate(data.startDate || null)
      })
      .catch(() => {
        const fallback = getFallbackBasho()
        setBasho(fallback)
        setDay(1)
        setStatus('unknown')
        setStartDate(null)
      })
  }, [])

  const daysUntil = localDaysUntil(startDate)
  return { basho, day, status, loading: status === 'loading', daysUntil, startDate }
}
